import { useState, useEffect } from 'react';
import { Save, RotateCcw, Info, Settings, Bot } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SettingsPage = ({ user }) => {
  const [settings, setSettings] = useState({
    app_title: '',
    app_subtitle: '',
    btn_guest_tab: '',
    btn_employee_tab: '',
    btn_guest_continue: '',
    samira_rules: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('general'); // 'general' or 'samira'

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return () => clearTimeout(timer);
    }
  }, [message.text]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/settings`);
      setSettings({
        app_title: response.data.app_title || '',
        app_subtitle: response.data.app_subtitle || '',
        btn_guest_tab: response.data.btn_guest_tab || '',
        btn_employee_tab: response.data.btn_employee_tab || '',
        btn_guest_continue: response.data.btn_guest_continue || '',
        samira_rules: response.data.samira_rules || ''
      });
    } catch (err) {
      console.error('Fehler beim Laden der Einstellungen:', err);
      setMessage({ type: 'error', text: 'Fehler beim Laden der Einstellungen' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings.app_title.trim() || !settings.app_subtitle.trim()) {
      setMessage({ type: 'error', text: 'Titel und Untertitel dürfen nicht leer sein' });
      return;
    }

    try {
      setSaving(true);
      await axios.put(`${API}/settings`, null, {
        params: {
          app_title: settings.app_title,
          app_subtitle: settings.app_subtitle,
          btn_guest_tab: settings.btn_guest_tab,
          btn_employee_tab: settings.btn_employee_tab,
          btn_guest_continue: settings.btn_guest_continue,
          samira_rules: settings.samira_rules,
          updated_by: user?.username || 'admin'
        }
      });

      setMessage({ type: 'success', text: 'Einstellungen erfolgreich gespeichert!' });

      // Nur bei allgemeinen Einstellungen neu laden
      if (activeTab === 'general') {
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setMessage({ type: 'error', text: 'Fehler beim Speichern der Einstellungen' });
    } finally {
      setSaving(false);
    }
  };

  const handleResetGeneral = () => {
    setSettings(prev => ({
      ...prev,
      app_title: 'GB-Sylt-Wissen',
      app_subtitle: 'Wissens- & Chat-System',
      btn_guest_tab: 'Gast-Zugang',
      btn_employee_tab: 'Mitarbeiter',
      btn_guest_continue: 'Als Gast fortfahren'
    }));
    setMessage({ type: 'info', text: 'Standardwerte wiederhergestellt. Klicken Sie "Speichern" um die Änderungen zu übernehmen.' });
  };

  const handleResetSamira = () => {
    const defaultRules = `Samira – Verhaltensregeln

ANTWORTSTIL:
- Antworte immer auf Deutsch
- Freundlich, klar und professionell
- Keine Emojis verwenden
- Kurze, prägnante Antworten (1-2 Sätze)

ANSPRACHE JE ROLLE:
- Gast: Neutrale, höfliche Sie-Form (formelle Ansprache)
- Mitarbeiter: Du-Form in allen Antworten (kollegiale Ansprache)
- Admin: Du-Form in allen Antworten (kollegiale Ansprache)

ROLLENLOGIK:
- Gast: Nur Gast-Wissen verwenden, KEINE Quellenangabe
- Mitarbeiter: Gast-Wissen + Mitarbeiter-Wissen, MIT Quellenangabe
- Admin: Alle drei Wissensbereiche, MIT Quellenangabe

OUTPUT-CLEANING:
- Entferne Markdown-Überschriften (#, ##, ###)
- Entferne "Antwort:" Präfixe
- Entferne Trenner (---)
- Entferne die ursprüngliche Frage aus der Antwort
- Gib nur den inhaltlichen Antworttext zurück

WICHTIG:
- Nur den thematisch passenden Abschnitt aus dem Memo-Feld verwenden
- NIEMALS das komplette Memo-Feld ausgeben
- Quellenformat: "Quelle: <Wissensbereich> #<Nummer> – <Titel>"

FALLBACK:
Wenn kein passendes Wissen vorhanden ist, antworte:
- Für Gäste: "Zu diesem Thema liegt aktuell kein hinterlegtes Wissen vor."
- Für Mitarbeiter/Admin: "Zu diesem Thema liegt aktuell kein hinterlegtes Wissen vor. Du kannst gerne einen Admin kontaktieren."
`;
    setSettings(prev => ({ ...prev, samira_rules: defaultRules }));
    setMessage({ type: 'info', text: 'Standard-Verhaltensregeln wiederhergestellt. Klicken Sie "Speichern" um die Änderungen zu übernehmen.' });
  };

  const isGeneralTab = activeTab === 'general';
  const isSamiraTab = activeTab === 'samira';

  return (
    <div className="h-full overflow-y-auto bg-gray-100">
      <div className="max-w-3xl mx-auto p-4 lg:p-6 space-y-4">
        
        {/* Header */}
        <div className="bg-white rounded-lg border p-4 flex items-center gap-3">
          <div className="h-12 w-12 rounded-lg bg-gray-600 flex items-center justify-center">
            <Settings className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Einstellungen</h1>
            <p className="text-sm text-gray-500">Anzeige-Texte und Samira-Verhaltensregeln</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg border p-1 flex gap-1">
          <button
            type="button"
            onClick={() => setActiveTab('general')}
            className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              isGeneralTab 
                ? 'bg-blue-600 text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Settings className="h-4 w-4" />
            Allgemein
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('samira')}
            className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              isSamiraTab 
                ? 'bg-purple-600 text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Bot className="h-4 w-4" />
            Samira – Verhaltensregeln
          </button>
        </div>

        {/* Message */}
        <div style={{ minHeight: '60px' }}>
          <div 
            className={`p-4 rounded-lg border flex items-center justify-between transition-opacity duration-200 ${
              message.type === 'error' ? 'bg-red-100 text-red-800 border-red-200' :
              message.type === 'success' ? 'bg-green-100 text-green-800 border-green-200' :
              'bg-blue-100 text-blue-800 border-blue-200'
            }`}
            style={{ display: message.text ? 'flex' : 'none' }}
          >
            <span>{message.text}</span>
            <button 
              type="button" 
              onClick={() => setMessage({ type: '', text: '' })} 
              className="ml-4 p-1 hover:opacity-70 text-lg font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Loading */}
        <div style={{ display: loading ? 'block' : 'none' }} className="bg-white rounded-lg border p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-500">Lade Einstellungen...</p>
        </div>

        {/* General Settings Tab */}
        <div style={{ display: !loading && isGeneralTab ? 'block' : 'none' }}>
          {/* App Title Section */}
          <div className="bg-white rounded-lg border mb-4">
            <div className="p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Anwendungs-Titel</h2>
              <p className="text-sm text-gray-500">Texte im Header der Anwendung</p>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Haupttitel</label>
                <input
                  type="text"
                  value={settings.app_title}
                  onChange={(e) => setSettings({ ...settings, app_title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="GB-Sylt-Wissen"
                  maxLength={50}
                />
                <p className="text-xs text-gray-400 mt-1">{settings.app_title.length}/50 Zeichen</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Untertitel</label>
                <input
                  type="text"
                  value={settings.app_subtitle}
                  onChange={(e) => setSettings({ ...settings, app_subtitle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Wissens- & Chat-System"
                  maxLength={80}
                />
                <p className="text-xs text-gray-400 mt-1">{settings.app_subtitle.length}/80 Zeichen</p>
              </div>

              {/* Preview */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                <p className="text-xs font-semibold text-gray-500 mb-2">VORSCHAU:</p>
                <div className="flex items-center gap-3 bg-white p-3 rounded-lg border">
                  <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center">
                    <span className="text-white font-bold text-lg">GB</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {settings.app_title || 'Haupttitel'}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {settings.app_subtitle || 'Untertitel'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Button Labels Section */}
          <div className="bg-white rounded-lg border mb-4">
            <div className="p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Button-Beschriftungen</h2>
              <p className="text-sm text-gray-500">Texte auf der Login-Seite</p>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tab: Gast-Zugang</label>
                <input
                  type="text"
                  value={settings.btn_guest_tab}
                  onChange={(e) => setSettings({ ...settings, btn_guest_tab: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Gast-Zugang"
                  maxLength={30}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tab: Mitarbeiter</label>
                <input
                  type="text"
                  value={settings.btn_employee_tab}
                  onChange={(e) => setSettings({ ...settings, btn_employee_tab: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Mitarbeiter"
                  maxLength={30}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Button: Als Gast fortfahren</label>
                <input
                  type="text"
                  value={settings.btn_guest_continue}
                  onChange={(e) => setSettings({ ...settings, btn_guest_continue: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Als Gast fortfahren"
                  maxLength={40}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-5 w-5" />
                )}
                {saving ? 'Speichere...' : 'Änderungen speichern'}
              </button>
              <button
                type="button"
                onClick={handleResetGeneral}
                disabled={saving}
                className="py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 font-medium flex items-center gap-2"
              >
                <RotateCcw className="h-5 w-5" />
                Zurücksetzen
              </button>
            </div>
          </div>
        </div>

        {/* Samira Rules Tab */}
        <div style={{ display: !loading && isSamiraTab ? 'block' : 'none' }}>
          <div className="bg-white rounded-lg border mb-4">
            <div className="p-4 border-b bg-purple-50">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-purple-600 flex items-center justify-center">
                  <Bot className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Samira – Verhaltensregeln</h2>
                  <p className="text-sm text-gray-600">Steuert wie Samira antwortet (Systemprompt)</p>
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-800">
                <strong>Hinweis:</strong> Diese Regeln steuern den Antwortstil, die Rollenlogik und das Output-Cleaning von Samira. 
                Änderungen sind sofort wirksam – kein Deploy notwendig.
              </div>
              
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verhaltensregeln (Memo-Feld)
              </label>
              <textarea
                value={settings.samira_rules}
                onChange={(e) => setSettings({ ...settings, samira_rules: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none resize-none"
                rows={20}
                placeholder="Verhaltensregeln für Samira eingeben..."
              />
              <p className="text-xs text-gray-400 mt-1">{settings.samira_rules.length} Zeichen</p>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start gap-3 mb-4">
            <Info className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-purple-800">
              <p className="font-medium mb-1">Wichtige Bereiche:</p>
              <ul className="list-disc list-inside space-y-1 text-purple-700">
                <li><strong>ANTWORTSTIL:</strong> Deutsch, freundlich, keine Emojis</li>
                <li><strong>ROLLENLOGIK:</strong> Wer darf was sehen</li>
                <li><strong>OUTPUT-CLEANING:</strong> Was aus Antworten entfernt wird</li>
                <li><strong>FALLBACK:</strong> Antwort wenn kein Wissen vorhanden</li>
              </ul>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-5 w-5" />
                )}
                {saving ? 'Speichere...' : 'Verhaltensregeln speichern'}
              </button>
              <button
                type="button"
                onClick={handleResetSamira}
                disabled={saving}
                className="py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 font-medium flex items-center gap-2"
              >
                <RotateCcw className="h-5 w-5" />
                Standard
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};
